import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getSummarySession1 } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { CheckCircle2, Sparkles, Trophy, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";

const KIND_LABELS = {
  phq9: "PHQ-9 (Depresión)",
  gad7: "GAD-7 (Ansiedad)",
  ghq28: "GHQ-28 (Salud general)",
  bhs: "BHS (Desesperanza)",
  swls: "SWLS (Satisfacción)",
  perma: "PERMA (Bienestar)",
};

export default function SummaryStep() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        setData(await getSummarySession1());
      } catch {
        setData({ questionnaires: [], smart_goals: { goals: [] }, quizzes: [] });
      }
    })();
  }, []);

  const chartData = (data?.questionnaires || []).map(q => ({
    name: KIND_LABELS[q.kind] || q.kind,
    score: q.score,
  }));

  return (
    <div data-testid="summary-step">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-2xl bg-[var(--ha-primary)]/10 grid place-items-center">
          <Trophy className="w-6 h-6 text-[var(--ha-primary)]" strokeWidth={1.75} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.16em] text-[var(--ha-muted)] font-bold">Sesión 1 completada</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-[var(--ha-text)]">Buen trabajo. Aquí tienes tu resumen.</h2>
        </div>
      </div>

      <div className="mt-8 grid gap-5">
        {/* Chart */}
        <Card className="rounded-2xl border-[var(--ha-border)] bg-white">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">Puntuaciones · Evaluación inicial</p>
            <h3 className="mt-1 font-bold text-[var(--ha-text)]">Tu línea base emocional</h3>
            <div className="mt-4 h-72">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--ha-border)" />
                    <XAxis dataKey="name" stroke="var(--ha-muted)" fontSize={11} interval={0} angle={-15} textAnchor="end" height={60} />
                    <YAxis stroke="var(--ha-muted)" fontSize={11} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid var(--ha-border)" }} />
                    <Bar dataKey="score" fill="var(--ha-primary)" radius={[8,8,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-[var(--ha-muted)] text-center pt-16">Sin datos aún.</p>
              )}
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(data?.questionnaires || []).map(q => (
                <div key={q.kind} className="flex items-center justify-between rounded-xl border border-[var(--ha-border)] px-4 py-2.5">
                  <span className="text-sm text-[var(--ha-text)] font-semibold">{KIND_LABELS[q.kind] || q.kind}</span>
                  <span className="text-sm text-[var(--ha-muted)]">{q.interpretation} · <span className="text-[var(--ha-primary)] font-bold">{q.score}</span></span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Goals */}
        <Card className="rounded-2xl border-[var(--ha-border)] bg-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-[var(--ha-secondary)]" strokeWidth={1.75} />
              <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">Tus objetivos SMART</p>
            </div>
            <ul className="mt-2 space-y-2">
              {(data?.smart_goals?.goals || []).map((g, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[var(--ha-text)]">
                  <CheckCircle2 className="w-4 h-4 text-[var(--ha-primary)] mt-0.5" strokeWidth={1.75} />
                  <span>{g.text}</span>
                </li>
              ))}
              {(!data?.smart_goals?.goals || data.smart_goals.goals.length === 0) && (
                <li className="text-sm text-[var(--ha-muted)]">Aún no has definido objetivos.</li>
              )}
            </ul>
          </CardContent>
        </Card>
      </div>

      <div className="mt-10 flex justify-end">
        <Button
          data-testid="summary-back-dashboard"
          onClick={() => navigate("/dashboard")}
          className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6"
        >
          <Home className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Volver al panel
        </Button>
      </div>
    </div>
  );
}
